import { Fragment, type ReactNode } from 'react';
import type { FilterChip } from './filter-chips';

/** 컨트롤이 놓인 표면이 주는 능력 — 지금은 닫기 하나(팝오버는 실제 닫기, 드로워 스택은 no-op). */
export interface FilterFieldSurface {
  /**
   * 이 표면 닫기. **값 하나로 확정되는 컨트롤**(단일 select·2지선다 토글·단일 날짜)이 변경 직후 호출한다
   * (`SelectField` 즉시 닫힘과 같은 결). 복수 선택(인버터)·복수 필드(기간 시작~종료)는 호출하지 않는다 —
   * 첫 조작에 닫히면 나머지를 못 고른다. **어떤 조작이 "끝"인지는 컨트롤만 안다**(라벨 변화로 추측하지 않는다).
   */
  close: () => void;
  /**
   * 이 표면이 **live** 인가 — 변경이 곧바로 Applied(=URL)로 나가는가. 칩 팝오버=true / 드로워 스택=false.
   *
   * **연속 입력(텍스트)에서 갈린다.** live 에서 키 입력마다 `apply` 하면 URL 갱신 → 라우터 리렌더 → input 값이
   * Applied 에서 되돌아오며 **한글 IME 조합이 끊긴다**(자소분리). 텍스트 컨트롤은 live 표면에서만 로컬 버퍼 +
   * 명시 커밋([조회]·Enter)으로 동작해야 한다. (web 현재 필터엔 텍스트 차원이 없어 소비처는 없다 — admin
   * `TextField` 가 이 플래그의 실사용처이고, 여기선 두 앱의 표면 계약을 같은 모양으로 둔다.)
   */
  live: boolean;
}

/**
 * 필터 한 **논리 차원의 컨트롤** — 칩(`FilterFieldSpec`, 투영)의 입력 짝. 같은 `key` 축을 공유해
 * 「칩 ↔ 컨트롤」이 한 차원으로 묶인다(`attachFieldControls`).
 *
 * **여전히 컨트롤은 feature 가 손으로 그린다**(필터 UI 규칙 ⑩ — 선언으로 input 을 그리는 엔진은 거부).
 * 여기서 더해지는 건 렌더 방식이 아니라 **주소(key)와 지연 렌더(render)** 뿐이다 — 그 덕에 같은 JSX 한 벌이
 * 조회조건 드로워(전체 스택)와 칩 팝오버(그 차원만)에 **중복 없이** 재사용된다.
 *
 * `render` 는 `Field`+`FieldLabel` 을 **포함한 완결 블록**을 돌려준다(차원 하나가 복수 필드일 수 있다 —
 * 예: 기간 = 시작일+종료일). 그래서 두 표면 모두 라벨을 그대로 얻고, 표면별 라벨 분기가 필요 없다.
 */
export interface FilterFieldControl {
  /** 차원 식별 — 칩 spec key 와 동일해야 칩에 결합된다. */
  key: string;
  /** 차원명(UI_TEXT) — 오버레이 a11y. */
  label: string;
  /**
   * **팝오버 동반 그룹** — 같은 값을 주면 그 차원들이 칩 팝오버에 **함께** 뜬다(드로워 스택은 종전대로 각자 한 번).
   * 서로가 서로를 바꾸는 짝(날짜기준 ↔ 조회월: 기준을 바꾸면 값이 cascade 리셋된다)을 한 차원만 띄우면,
   * 기준을 바꾼 뒤 값을 고치러 다른 칩을 또 열어야 한다. 칩은 그대로 둘이다 — 정보량·엑셀 조회조건 유지.
   */
  group?: string;
  /** 컨트롤 블록(지연 렌더 — 표면이 필요할 때만 그린다). */
  render: (surface: FilterFieldSurface) => ReactNode;
}

export interface FilterFieldStackProps {
  fields: FilterFieldControl[];
  className?: string;
}

/**
 * **인라인(스택) 표면** — 닫을 것이 없다(드로워는 푸터 [닫기]가 소유). 컨트롤이 `close()` 를 불러도 no-op 이라
 * 같은 JSX 가 두 표면에서 그대로 동작한다. 모듈 상수라 렌더마다 새 객체가 생기지 않는다.
 */
export const INLINE_FIELD_SURFACE: FilterFieldSurface = { close: () => undefined, live: false };

/**
 * 조회조건 드로워 본문 — 컨트롤 세로 스택. `Fragment` 로만 감싸므로 차원이 복수 `Field` 를 내도
 * flex 자식 자격이 유지된다(= 필드 사이 gap 이 종전과 동일).
 */
export function FilterFieldStack({ fields, className }: FilterFieldStackProps) {
  return (
    <div className={className ?? 'flex flex-col gap-5'}>
      {fields.map((field) => (
        <Fragment key={field.key}>{field.render(INLINE_FIELD_SURFACE)}</Fragment>
      ))}
    </div>
  );
}

/**
 * 칩 ← 컨트롤 결합 — 같은 `key` 의 컨트롤을 칩에 실어 **칩 클릭 = 그 차원만 편집**(pc 팝오버)을 연다.
 * `group` 이 있으면 그 그룹 전체를 함께 실어 준다(짝을 이루는 차원은 한 팝오버에서 같이 만진다).
 * 짝이 없는 칩은 그대로 통과(비대화형 칩 유지) — 결합·조건부 칩처럼 대응 컨트롤이 없는 차원이 있다.
 *
 * ⚠️ **live 필터 전용** — 팝오버 컨트롤은 Applied 를 직접 쓴다(드로워의 draft 단계를 건너뛴다).
 * staged 필터(푸터 [조회]로 커밋)에 결합하면 칩 편집만 즉시 반영돼 두 경로의 의미가 갈린다.
 */
export function attachFieldControls(
  chips: FilterChip[],
  fields: FilterFieldControl[],
): FilterChip[] {
  return chips.map((chip) => {
    const field = fields.find((f) => f.key === chip.key);
    if (!field) return chip;
    const group = field.group ? fields.filter((f) => f.group === field.group) : [field];
    return {
      ...chip,
      control: {
        label: field.label,
        render: (surface) => group.map((f) => <Fragment key={f.key}>{f.render(surface)}</Fragment>),
      },
    };
  });
}
